import * as React from 'react';
import { Grid } from '@mui/material';
import { Swipe } from './swipe';
import AppService, { align } from '../services/app.service';

interface Props {
  id?: string;
  dataLength?: number;
  page?: number;
  pageCount?: number;
  spacing?: number;
  cardCount?: number;
  cardBorderWidth?: number;
  cardAlign?: align;
  swipe?: string;
  getCardWidth?: (value: number) => void;
  onChange?: (value: string) => void;
  children?: React.ReactNode;
}

export const Gallery: React.FunctionComponent<Props> = (props) => {
  const galleryRef = React.useRef<any>(null);
  const isMobile = AppService.isMobile();
  const isHorizontal = props.swipe?.startsWith('horizontal');
  const spacing = props.spacing || (isHorizontal? 1.5 : 0.5);
  const cardCount = props.cardCount === 1? 1 : (isMobile && isHorizontal? 2 : props.cardCount || 6);
  const [ref, setRef] = React.useState(galleryRef.current);
  const [cardWidth, setCardWidth] = React.useState(0);
  const [lastMove, setLastMove] = React.useState(0);
  const [maxMove, setMaxMove] = React.useState(0);
  const [isMoving, setIsMoving] = React.useState(false);
  const [isCanScroll, setIsCanScroll] = React.useState(true);
  const [isMaxMove, setIsMaxMove] = React.useState(false);

  const moving = async(isNext?: boolean) => {      
    if (isMoving || !props.swipe || !props.page || !props.pageCount) return;

    const page = props.page;
    const pageCount = props.pageCount;
    await setIsMoving(true);
    
    const refRect = ref?.getBoundingClientRect();
    const cardPos = isHorizontal?
    (!isNext && ref?.scrollLeft===0? 'first' : isNext && Math.round(ref?.scrollWidth - ref?.scrollLeft) <= ref?.clientWidth? 'last' :'') :
    (!isNext && ref?.scrollTop===0 && refRect.top > 0? 'first' : isNext && Math.round(ref?.scrollHeight-ref?.scrollTop) <= ref?.clientHeight && refRect.bottom < window.innerHeight? 'last' :'');
    
    if (cardPos) await props.onChange?.(cardPos);
    
    var move = 
      cardPos === 'first'? (page === 1? 0 : maxMove) :
      cardPos === 'last'? (page === pageCount? maxMove : 0) :
      lastMove + (isNext? 1 : -1) * (isHorizontal? ref?.clientWidth : ref?.clientHeight); 
      
    if (move > maxMove) move = maxMove; 
    else if (move < cardWidth) move = 0;
    await ref?.scrollTo({left: isHorizontal? move : 0, top: isHorizontal? 0 : move, behavior: cardPos? 'auto' : 'smooth'});
    setLastMove(move);
    
    setTimeout(() => setIsMoving(false), 200);
  }

  const onChange = (value: string) => {
    if (value.startsWith('end')) moving(value.endsWith('left') || value.endsWith('up'));
    props.onChange?.(value);
  }

  const onWheel = async(e: React.WheelEvent<HTMLElement>) => {
    if (!isMobile && props.swipe) moving(e.deltaY > 0);
  }

  const onMouseEnter = () => {
    if (!props.swipe || isMobile || !isHorizontal || (isHorizontal && !isCanScroll)) 
      return;

    if (document.body.clientHeight> window.innerHeight)
      document.body.style.paddingRight = '10px';
    document.body.style.overflow = 'hidden';
    setIsMaxMove(false);
  }

  const onMouseLeave = async() => {
    setIsMaxMove(true);
    setTimeout(() => {
      document.body.style.overflow = '';      
      document.body.style.paddingRight = '';        
    }, 1);
  }

  const onResize = React.useCallback(() => {moving()}, []);
  
  React.useEffect(() => {

    setTimeout(() => {
      setRef(galleryRef.current);
      if (ref?.clientWidth > 0) {
        var cardwidth = (ref?.children.length > 0? (ref?.clientWidth / (isHorizontal? cardCount : 1)) - spacing * (isHorizontal? 8 : 1) : 0);
        props.getCardWidth?.(cardwidth);
        setCardWidth(cardwidth);
      }

      if (props.swipe && props.dataLength && ref?.children.length >= props.dataLength) {
        setIsCanScroll(maxMove > cardWidth);
        setMaxMove(isHorizontal? ref?.scrollWidth - ref?.clientWidth - (isMaxMove? 0 : 0): ref?.scrollHeight);
        if (ref?.scrollLeft <= 0 && maxMove > 0) moving();
      }
    }, 1);

  }, [props.cardCount, props.dataLength, cardWidth, ref?.scrollLeft, ref?.scrollWidth, ref?.clientWidth, ref?.children.length])

  React.useEffect(() => {
    window.addEventListener('resize', onResize);

    return () => { 
      window.addEventListener('resize', onResize); 
      onMouseLeave();
    }
  }, [])
  
  
  return (
    <Swipe onChange={onChange} vertical={!isHorizontal}>
      <Grid ref={galleryRef} container={!!props.dataLength && props.dataLength < cardCount} justifyContent={props.cardAlign} display={isHorizontal? 'flex' : 'grid'} borderRadius={cardCount===1? 5 : -1} sx={{gap: spacing, pl: isHorizontal? 1 : 0, pr: isHorizontal? 1 : (props.dataLength && (props.dataLength > 1? 10 : 0)), pb: props.dataLength && (props.cardBorderWidth || (props.dataLength > 1? 1 : 0)), overflowX: isHorizontal? 'auto' : 'hidden', overflowY: isHorizontal? 'hidden' : 'auto', '::-webkit-scrollbar': {display: 'none'}}} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onWheel={onWheel}>
        {props.children}
        {!isMobile && isHorizontal && isMaxMove && isCanScroll && <Grid width={1} />}
      </Grid>
    </Swipe>
  );
}
